"use client";

import { useEffect, useRef, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { cx } from "@tripcart/traveler-ui/cx";

import { useTranslator } from "@/lib/translation-context";
import { scrollToResultsTop } from "./_scroll";
import styles from "./_pagination.client.module.css";

/**
 * Pagination navigation. Two behaviours that make paging feel instant:
 *  - prefetch the adjacent pages so a click paints from the router cache
 *    instead of a cold server round-trip;
 *  - scroll the results to the top only AFTER the new page commits (i.e.
 *    `current` changed following a click), never synchronously inside the
 *    click — which used to race the smooth scroll ahead of the server-fetched
 *    data and made the page "scroll first, then load".
 */
function usePaginationNavigation(
  current: number,
  total: number,
  hrefs: string[],
): (href: string) => (e: React.MouseEvent) => void {
  const router = useRouter();
  const [, startTransition] = useTransition();
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (current > 1) router.prefetch(hrefs[current - 2]!);
    if (current < total) router.prefetch(hrefs[current]!);
  }, [current, total, hrefs, router]);

  useEffect(() => {
    if (!navigatedRef.current) return;
    navigatedRef.current = false;
    scrollToResultsTop();
  }, [current]);

  return (href) => (e) => {
    e.preventDefault();
    navigatedRef.current = true;
    startTransition(() => {
      router.push(href, { scroll: false });
    });
  };
}

export function ExperiencePagination({
  current,
  hrefs,
}: {
  current: number;
  hrefs: string[];
}): React.ReactElement | null {
  const t = useTranslator();
  const total = hrefs.length;
  const go = usePaginationNavigation(current, total, hrefs);
  if (total <= 1) return null;

  const isPrevDisabled = current === 1;
  const isNextDisabled = current === total;

  const show = (p: number) =>
    p === 1 || p === total || Math.abs(p - current) <= 1;

  return (
    <nav role="navigation" aria-label={t("pagination")} className={styles.nav}>
      {/* Previous */}
      <PaginationNavButton
        href={isPrevDisabled ? undefined : hrefs[current - 2]}
        onClick={
          isPrevDisabled ? (e) => e.preventDefault() : go(hrefs[current - 2]!)
        }
        disabled={isPrevDisabled}
        aria-label={t("Go to Previous Page")}
      >
        <ChevronLeftIcon strokeWidth={2} />
        <span>{t("Previous")}</span>
      </PaginationNavButton>

      {/* Page numbers */}
      <div className={styles.pages}>
        {Array.from({ length: total }, (_, i) => i + 1).map((p) => {
          if (!show(p)) {
            return show(p - 1) ? (
              <span key={`e-${p}`} aria-hidden className={styles.ellipsis}>
                ···
              </span>
            ) : null;
          }

          const isActive = p === current;
          return (
            <a
              key={p}
              href={hrefs[p - 1]!}
              onClick={go(hrefs[p - 1]!)}
              aria-current={isActive ? "page" : undefined}
              aria-label={t("Page {n}", { n: p })}
              className={cx(
                styles.control,
                styles.page,
                isActive ? styles.pageActive : styles.pageIdle,
              )}
            >
              {p}
            </a>
          );
        })}
      </div>

      {/* Next */}
      <PaginationNavButton
        href={isNextDisabled ? undefined : hrefs[current]}
        onClick={
          isNextDisabled ? (e) => e.preventDefault() : go(hrefs[current]!)
        }
        disabled={isNextDisabled}
        aria-label={t("Go to Next Page")}
      >
        <span>{t("Next")}</span>
        <ChevronRightIcon strokeWidth={2} />
      </PaginationNavButton>
    </nav>
  );
}

function PaginationNavButton({
  href,
  onClick,
  disabled,
  children,
  ...props
}: {
  href?: string;
  onClick: (e: React.MouseEvent) => void;
  disabled: boolean;
  children: React.ReactNode;
} & React.AriaAttributes): React.ReactElement {
  const className = cx(
    styles.control,
    styles.step,
    disabled ? styles.stepDisabled : styles.stepEnabled,
  );
  // A disabled prev/next must NOT be an <a> without href — Lighthouse's
  // `crawlable-anchors` audit flags those and docks the SEO score. Render a
  // non-anchor <span> instead; `aria-disabled` keeps the state for AT.
  if (disabled) {
    return (
      <span aria-disabled="true" className={className} {...props}>
        {children}
      </span>
    );
  }
  return (
    <a href={href} onClick={onClick} className={className} {...props}>
      {children}
    </a>
  );
}
